import { useEffect, useRef, useState } from "react";
import {
  addConversation,
  getLastMessage as setLastMessageSentAt,
} from "../../api/endpoints/chat/conversations";
import ChatConversationView from "../../components/chat_v2/ChatConversationView";
import ChatNavigation from "../../components/chat_v2/ChatNavigation";
import ConversationModel, {
  ChatContact,
} from "../../api/models/ConversationModel";
import { ChatMessage } from "../../components/chat_v2/ChatMessagesList";
import { Conversation, Message } from "@twilio/conversations";
import { useChat } from "../../context/ChatContext";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { useTranslation } from "react-i18next";
import ChatConversationModal from "../../components/chat_v2/ChatConversationModal";
import { ApiResponse } from "../../api/apiResponse";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Location, Params, useLocation, useParams } from "react-router-dom";
import moment from "moment-timezone";

export default function ChatPage() {
  const LocalBasePath = "page.chat";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [draftMessage, setDraftMessage] = useState<string | undefined>();
  const messageBoxInputRef = useRef<HTMLTextAreaElement>(null);
  const messageBoxModalInputRef = useRef<HTMLTextAreaElement>(null);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(false);
  const [isChatConversationModalopen, setIsChatConversationModalopen] =
    useState<boolean>(false);

  const location = useLocation();
  const paramsRef = useRef<string | undefined | null>();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    paramsRef.current = params.get("conversation_id");
  }, [location]);

  const {
    isInitialized,
    getConversationMessages,
    initializeChat,
    getConversation,
    getUnreadCount,
    selectedConversation,
    setSelectedConversation,
    selectedConversationRef,
    currentActorTwillioIdentity,
    contacts,
    conversations,
    messages,
    messagesRef,
    isLoadingMessages,
    setIsLoadingMessages,
    refreshContacts,
    refreshConversations,
    setMessages,
    conversationContacts,
    newContacts,
    filteredConversationContacts,
    filteredNewContacts,
    search,
    resetSearchResults,
    currentActor,
    prependConversation,
    resetMessages,
    scrollContactCardIntoView,
    groupedMessages,
  } = useChat();

  useEffectOnce(
    () => initializeChat(handleChatInitialized, onMessageAddedHandler),
    true
  );

  const handleChatInitialized = () => {
    refreshConversations();
    refreshContacts();
    if (!!paramsRef.current) {
      onSelectConversation(paramsRef.current);
      setIsChatConversationModalopen(true);
    }
  };

  const onSearch = (query: string): void => {
    if (query === "") {
      resetSearchResults();
      setShowSearchResults(false);
    } else {
      search(query);
      setShowSearchResults(true);
    }
  };

  const onSelectConversation = async (conversationId: string) => {
    resetDraftMessage();
    setIsLoadingMessages(true);
    const conversation: Conversation = await getConversation(conversationId);
    setSelectedConversation(conversation);
    refreshConversationMessages(conversationId);
    setIsLoadingMessages(false);
    setIsChatConversationModalopen(true);
    await conversation!.setAllMessagesRead();
    refreshConversations()
  };

  const refreshConversationMessages = async (conversationId: string) => {
    const fetchedMessages: ChatMessage[] = await getConversationMessages(
      conversationId
    );
    setMessages(fetchedMessages);
  };

  const onMessageAddedHandler = (message: Message) => {
    const convId = message.conversation.sid;
    // If the message was added to the selected conversation then we only refresh the message because refreshing
    // the conversations list order should already be handled in the onSendDraftMessage method
    // (One cavite this will not work for receiving a message on selected conversation)
    // We do this because refreshing the conversations before api call to setLastMessageSentAt is not done
    // then we will get an incorrect order
    if (convId === selectedConversationRef.current?.sid) {
      const newMessage: ChatMessage = {
        message: message.body ?? "No Message",
        direction:
          message.author === currentActorTwillioIdentity ? "sent" : "received",
        date: message.dateCreated ?? moment().toDate(),
      };
      setMessages([...messagesRef.current, newMessage]);
    } else {
      refreshConversations();
    }
  };

  const onSelectContact = async (contact: ChatContact) => {
    setIsLoadingContacts(true);
    const response: ApiResponse = await addConversation(currentActor, {
      participant_id: contact.id,
      participant_type: contact.userType,
    });
    if (response.isSuccess) {
      const conversationModel: ConversationModel =
        ConversationModel.fromApiResponse(response.data);
      const conversation: Conversation = await getConversation(
        conversationModel.twilioId
      );
      refreshContacts();
      resetMessages();
      resetDraftMessage();
      resetSearchResults();
      setShowSearchResults(false);
      setSelectedConversation(conversation);
      prependConversation(conversationModel);
      scrollContactCardIntoView(conversation.sid);
    } else {
      toast.error("Unable to start conversation with contact");
    }
    setIsLoadingContacts(false);
  };

  const resetDraftMessage = () => {
    setDraftMessage(undefined);
    if (!!messageBoxInputRef.current) messageBoxInputRef.current!.value = "";
    if (!!messageBoxModalInputRef.current)
      messageBoxModalInputRef.current!.value = "";
  };

  const onMessageChange = (value: string) => {
    setDraftMessage(value);
  };

  const onSendDraftMessage = async () => {
    if (!draftMessage) return;
    const sanatizedMessage = draftMessage.trim();
    if (sanatizedMessage === "") return;
    const conversationModel: ConversationModel = conversations.find(
      (con) => con.twilioId === selectedConversation!.sid!
    )!;
    // Send message on Twilio
    await selectedConversation!.sendMessage(sanatizedMessage);
    // Reset input box
    resetDraftMessage();
    // Set all messages as read
    await selectedConversation!.setAllMessagesRead();
    // Update last message sent at date
    await setLastMessageSentAt({
      conversation_id: conversationModel.id,
    });
    // Refetch all conversation to get the new order
    refreshConversations();
  };

  if (!isInitialized) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <LoadingSpinner size={100} />
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full overflow-hidden">
      <div className="w-full md:w-[35%] overflow-y-auto  h-[calc(100vh-112px)]">
        <ChatNavigation
          conversationsList={
            showSearchResults
              ? filteredConversationContacts
              : conversationContacts
          }
          contactsList={showSearchResults ? filteredNewContacts : newContacts}
          onSearch={onSearch}
          onSelectConversation={onSelectConversation}
          onSelectContact={onSelectContact}
          selectedConversation={selectedConversation}
          isLoadingContacts={isLoadingContacts}
        />
      </div>
      <div className="w-full md:w-[65%] relative px-1 hidden md:block">
        {!!selectedConversation ? (
          <ChatConversationView
            messageGroups={groupedMessages}
            isLoadingMessages={isLoadingMessages}
            onMessageChange={onMessageChange}
            onSendDraftMessage={onSendDraftMessage}
            messageBoxInputRef={messageBoxInputRef}
          />
        ) : (
          <div className="w-full h-full flex justify-center items-center">
            <p>{t("noConversationSelected")}</p>
          </div>
        )}
      </div>
      <div className="block  md:hidden relative">
        <ChatConversationModal
          open={isChatConversationModalopen}
          setOpen={setIsChatConversationModalopen}
          messageGroups={groupedMessages}
          isLoadingMessages={isLoadingMessages}
          onMessageChange={onMessageChange}
          onSendDraftMessage={onSendDraftMessage}
          messageBoxInputRef={messageBoxModalInputRef}
        />
      </div>
    </div>
  );
}
